import { create } from 'zustand';
import { Account } from './types';

interface AccountState {
  accounts: Account[];
  currentAccount: Account | null;
  isLoading: boolean;
  error: string | null;
  
  setAccounts: (accounts: Account[]) => void;
  addAccount: (account: Account | Omit<Account, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateAccount: (id: string, updates: Partial<Account>) => void;
  deleteAccount: (id: string) => void;
  setCurrentAccount: (account: Account | null) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useAccountStore = create<AccountState>((set) => ({
  accounts: [],
  currentAccount: null,
  // true до первого ответа сервера: иначе пустой стор читается как «аккаунтов нет»
  // и на кадр показывается экран «Нет подключенных аккаунтов». DataProvider снимает в finally.
  isLoading: true,
  error: null,
  
  // Скрываем только phone-ПУСТЫШКИ: записи, заведённые phone-sync'ом по одному
  // имени, без accessToken — они ломали счётчики и висели в списках зря.
  // Аккаунт, подключённый через OAuth и позже усыновлённый phone-sync
  // (connectionType→phone, но токен и счётчики на месте), раньше пропадал:
  // в списке его нет, а повторное добавление отбивалось как «уже добавлен»
  // (@steppe.daily). Такие показываем — со своим phone-бейджем.
  setAccounts: (accounts) => set({
    accounts: accounts
      .filter(acc => acc.connectionType !== 'phone' || Boolean(acc.accessToken))
      .map(acc => ({
        ...acc,
        tags: acc.tags || [],
      }))
  }),
  
  addAccount: (account) => 
    set((state) => {
      // Если уже есть ID, значит это полный объект Account с сервера
      if ('id' in account && account.id) {
        return { 
          accounts: [...state.accounts, {
            ...account,
            createdAt: account.createdAt instanceof Date ? account.createdAt : new Date(account.createdAt),
            updatedAt: account.updatedAt instanceof Date ? account.updatedAt : new Date(account.updatedAt),
            accessTokenExpiry: account.accessTokenExpiry instanceof Date ? account.accessTokenExpiry : new Date(account.accessTokenExpiry),
            tags: account.tags || [],
          }]
        };
      }
      
      // Иначе создаем новый с автогенерацией полей
      return { 
        accounts: [...state.accounts, { 
          ...account, 
          id: Date.now().toString(), 
          createdAt: new Date(), 
          updatedAt: new Date(),
          instagramUserId: account.instagramUserId || '',
          igId: account.igId || '',
          accessTokenExpiry: account.accessTokenExpiry || new Date(Date.now() + 60 * 24 * 60 * 60 * 1000),
          isActive: account.isActive !== undefined ? account.isActive : true,
          tags: account.tags || [],
          followersCount: account.followersCount || 0,
          followsCount: account.followsCount || 0,
          mediaCount: account.mediaCount || 0,
          biography: account.biography || '',
          website: account.website || '',
        }] 
      };
    }),
  
  updateAccount: (id, updates) =>
    set((state) => ({
      accounts: state.accounts.map((acc) =>
        acc.id === id ? { ...acc, ...updates, updatedAt: new Date() } : acc
      ),
    })),
  
  deleteAccount: (id) =>
    set((state) => ({
      accounts: state.accounts.filter((acc) => acc.id !== id),
    })),
  
  setCurrentAccount: (account) => set({ currentAccount: account }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
}));